// THE OLD ADDRESSES, ONTO THE MAP. Notifications, the consent flow's
// redirect_uri, bookmarks and the docs still carry the addresses Nucleus had
// before the map (/privacy-realms, /manage, /group/<id>/members?pane=share …).
// Each lands on the page that now holds the same thing; what the old address
// said (?realm=, ?app=, ?node= …) becomes the new page's ?open=.
//
// Returns the new address, or null when the address is not an old one.

const GROUP_PANES = {
    share: ['self', 'share'],
    settings: ['self', 'settings'],
    public: ['self', 'public'],
    invitations: ['people', 'invitations'],
    apps: ['apps', null],
    realms: ['realms', null],
    members: ['people', 'members'],
};

const GRAPH_TABS = { trash: 'trash', proposals: 'review', questions: 'review', schemas: 'schemas' };

// The old leaf names, and the map's own page ids: the notification resolver
// still says ?leaf=security, and a leaf this table did not know fell through to
// Settings' first page — a password change opened Profile. Each is [section,
// page, query]: a setting lands on the tab that holds it.
const SELF_LEAVES = {
    profile: ['self', 'profile'], identities: ['self', 'profile'], notifications: ['self', 'notifications'],
    timezone: ['self', 'profile'], session: ['self', 'security', 'tab=settings'],
    recovery: ['self', 'security', 'tab=settings'], security: ['self', 'security'],
    encryption: ['self', 'data'], trash: ['self', 'data'], data: ['self', 'data'],
    appearance: ['self', 'appearance'], public: ['people', 'public'],
};

function build(groupId, section, page, query) {
    const base = `${groupId ? `/group/${groupId}` : ''}/${section}${page ? `/${page}` : ''}`;
    const q = query.toString();
    return q ? `${base}?${q}` : base;
}

export function legacyTarget(pathname, search = '') {
    const to = rewrite(pathname, search);
    // never send an address to itself (a Navigate to itself loops forever)
    if (to && to === `${pathname}${search && search !== '?' ? search : ''}`) return null;
    return to;
}

function rewrite(pathname, search) {
    const q = new URLSearchParams(search);
    const parts = pathname.split('/').filter(Boolean);
    let groupId = null;
    let rest = parts;
    let fromQuery = false;
    if (parts[0] === 'group' && parts[1]) {
        groupId = parts[1];
        rest = parts.slice(2);
    }
    if (!groupId && q.get('group_id')) {
        fromQuery = true;
        groupId = q.get('group_id');
        q.delete('group_id');
    }
    const head = rest[0] || '';
    const move = (from, to) => { if (q.get(from)) { q.set(to, q.get(from)); q.delete(from); } };

    switch (head) {
        case '': // /group/<id> — the group's own front door
            return groupId ? build(groupId, 'graph', null, q) : null;
        case 'privacy-realms':
        case 'exposure-profiles':
        case 'privacy': {
            if (q.get('entity')) { move('entity', 'open'); return build(groupId, 'realms', 'profiles', q); }
            const tab = q.get('tab');
            q.delete('tab');
            move('realm', 'open');
            if (tab === 'who') return build(groupId, 'realms', 'profiles', q);
            if (tab === 'sessions') return build(null, 'self', 'security', q);
            return build(groupId, 'realms', tab === 'overview' ? 'profiles' : null, q);
        }
        case 'access': { // Access joined Realms as Privacy & Access: its pages are filters of Exposure profiles
            const KIND = { apps: 'apps', people: 'people', groups: 'groups', agents: 'agents' };
            if (KIND[rest[1]]) q.set('kind', KIND[rest[1]]);
            if (rest[1] === 'ended') q.set('status', 'inactive');
            return build(groupId, 'realms', 'profiles', q);
        }
        case 'applications':
            move('app', 'open');
            return build(groupId, 'apps', null, q);
        case 'apps':
            if (rest[1]) return null; // already a map address
            if (!q.get('app')) return null;
            move('app', 'open');
            return build(groupId, 'apps', null, q);
        case 'authorized-apps':
            return build(null, 'apps', null, q);
        case 'billing':
            return build(null, 'apps', 'billing', q);
        case 'automations':
            return build(groupId, 'plugins', 'automations', q);
        case 'manage':
            q.delete('tab');
            return build(null, 'people', null, q);
        case 'members': {
            const pane = q.get('pane');
            q.delete('pane');
            const [section, page] = GROUP_PANES[pane] || ['people', 'members'];
            return build(groupId, section, page, q);
        }
        case 'settings':
        case 'sharing':
        case 'invitations': {
            const [section, page] = GROUP_PANES[head === 'sharing' ? 'share' : head];
            return groupId ? build(groupId, section, page, q) : build(null, 'self', null, q);
        }
        case 'nodes':
            return groupId ? build(groupId, 'graph', null, q) : null;
        case 'schemas':
            return build(groupId, 'graph', 'schemas', q);
        case 'trash':
            return build(groupId, 'graph', 'trash', q);
        case 'proposals':
            return build(groupId, 'graph', 'review', q);
        case 'graph': {
            if (rest[1]) return null;
            const tab = q.get('tab');
            if (!tab && !q.get('node')) return null;
            q.delete('tab');
            move('node', 'open');
            return build(groupId, 'graph', GRAPH_TABS[tab] || null, q);
        }
        case 'history':
            return build(groupId, 'activity', null, q);
        case 'inbox':
            return build(groupId, 'activity', 'inbox', q);
        case 'activity': {
            // Activity › Proposals joined the Graph's Review (its All lane)
            if (rest[1] === 'proposals') return build(groupId, 'graph', 'review', q);
            if (rest[1]) return null;
            const view = q.get('view');
            // only an old dialect is rewritten: ?view=inbox, or ?group_id= for the space
            if (!fromQuery && view !== 'inbox') return null;
            q.delete('view');
            return build(groupId, 'activity', view === 'inbox' ? 'inbox' : null, q);
        }
        case 'people': {
            // Requests joined Friends (friend requests) and Following (follow
            // requests); the old page and ?people=requests land on Friends
            if (rest[1] !== 'requests' && q.get('people') !== 'requests') return null;
            q.delete('people');
            return build(groupId, 'people', null, q);
        }
        case 'self': {
            if (rest[1]) return null;
            const leaf = q.get('leaf');
            if (!leaf) return null;
            q.delete('leaf');
            const [section, page, extra] = SELF_LEAVES[leaf] || ['self', null];
            new URLSearchParams(extra || '').forEach((v, k) => q.set(k, v));
            return build(null, section, page, q);
        }
        default:
            return null;
    }
}
